import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import {
  ArticleRating,
  InsuranceProductsResponse,
  UserInsuranceProduct,
} from 'src/app/interfaces/api';
import {
  DashboardCard,
  HelplineData,
  IOfferCard,
  IOffers,
  ProtectAndSaveProduct,
  StudentLifeArticleCard,
  StudentWellbeing,
  IDocument,
  IStrapiDocument,
  IStrapiDocuments,
  ICYCDashboardBanner,
  IInternationalStudentPage,
  IJobsAndCareers,
} from 'src/app/interfaces/strapi';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services/auth.service';

import { PageComponent } from 'src/app/shared/components/page/page.component';
import { User } from 'src/app/shared/models/user.model';
import StrapiService from 'src/app/services/strapi.service';
import IDashboard from 'src/app/interfaces/strapi/dashboard.interface';
import { NgIf, NgClass } from '@angular/common';
import { UniService } from '../../../services/uni.service';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { PageRetrievalErrorComponent } from '../../../shared/components/page-retrieval-error/page-retrieval-error.component';
import { PartnerBannerComponent } from '../partner-banner/partner-banner.component';
import { BannerComponent } from '../../../shared/components/banner/banner.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { ConfirmYourCoverComponent } from '../confirm-your-cover/confirm-your-cover.component';
import { OnboardingComponent } from '../onboarding/onboarding.component';
import { StudentWellbeingCarouselComponent } from '../../../shared/components/student-wellbeing-carousel/student-wellbeing-carousel.component';
import { StudentGuardBannerComponent } from '../student-guard-banner/student-guard-banner.component';
import { StudentLifeCarouselComponent } from '../student-life-carousel/student-life-carousel.component';
import { VerticalCarouselComponent } from '../../../shared/components/vertical-carousel/vertical-carousel.component';
import { JobsAndCareersCarouselSlideComponent } from '../jobs-and-careers/jobs-and-careers-carousel-slide/jobs-and-careers-carousel-slide.component';
import { ProtectAndSaveDashboardComponentComponent } from '../protect-and-save/protect-and-save-dashboard-component/protect-and-save-dashboard-component.component';
import { MyInsuranceSectionComponent } from '../my-insurance-section/my-insurance-section.component';

enum AlternateResidence {
  livingAtHome = 'im-living-at-home',
  privateRental = 'i-have-a-private-let-or-rental',
}

enum DashboardEnum {
  ISRoute = '/app/international-student',
  wellbeingRoute = 'app/wellbeing',
  JACRoute = 'app/jobs-and-careers',
  topOffersRoute = 'app/top-offers',
  studentLifeRoute = 'app/student-life',
  protectAndSaveRoute = 'app/protect-and-save',
  myInsuranceRoute = 'app/my-insurance',
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    imports: [
        BreadcrumbsComponent,
        NgIf,
        PageRetrievalErrorComponent,
        NgClass,
        PartnerBannerComponent,
        BannerComponent,
        ModalComponent,
        ConfirmYourCoverComponent,
        OnboardingComponent,
        StudentWellbeingCarouselComponent,
        StudentGuardBannerComponent,
        StudentLifeCarouselComponent,
        VerticalCarouselComponent,
        JobsAndCareersCarouselSlideComponent,
        ProtectAndSaveDashboardComponentComponent,
        MyInsuranceSectionComponent,
    ],
})
export class DashboardComponent extends PageComponent implements OnInit {
  public override pageData: IDashboard = <IDashboard>{};

  public DashboardEnum = DashboardEnum;

  public showWellbeing = true;

  public showCYCBanner = false;

  public showCYCModal = false;

  public protectAndSaveProducts: DashboardCard[] = [];

  public studentWellbeingCards: any[] = [];

  public studentLifeCards: StudentLifeArticleCard[] = [];

  public latestOffersCards: DashboardCard[] = [];

  public helplineData!: HelplineData;

  public cycDashboardBanner: ICYCDashboardBanner = <ICYCDashboardBanner>{};

  public isLoading: boolean = false;

  public insuranceProducts: UserInsuranceProduct[] = [];

  public showUniPartnerBanner: boolean = false;

  public showAccommodationPartnerBanner: boolean = false;

  public accommodationPartnerId: string | null | undefined = '';

  public uniPartnerId: string | null | undefined = '';

  public articleRatings: ArticleRating[] = [];

  public shouldShowOnboarding = false;

  public shouldShowCYC = false;

  public internationalStudentPageData: IInternationalStudentPage = <
    IInternationalStudentPage
  >{};

  public jobsAndCareersCards: DashboardCard[] = <DashboardCard[]>{};

  public showInternationalStudentBanner: boolean = false;

  public showInternationalStudentCarousel: boolean = false;

  public showDashboardInsuranceSection: boolean = false;

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    public readonly auth: AuthService,
    private readonly router: Router,
    private readonly userService: UserService,
    public readonly uniService: UniService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(titleService, metaService, strapi);
  }

  private deferUpdate(fn: () => void): void {
    Promise.resolve().then(() => {
      fn();
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.isLoading = true;

    // change to the uni and accommodation are stored in the DB
    // We need to force user refresh everytime the user hits the dashboard
    this.userService.refreshUser().subscribe((res) => {
      // to stop the my-insurance button in the header blinking as new insurance total is calculated.
      this.auth.currentUser = { ...this.auth.currentUser, ...res.data };
      this.getInsuranceProducts();

      this.auth.afterUserUpdate();

      if (this.auth.currentUser?.accommodation) {
        this.deferUpdate(() => {
          this.handlePageData(this.auth.currentUser!);
        });
      }
    });

    this.strapi
      .getDashboard()
      .subscribe((data: IStrapiDocument<IDashboard>) => {
        const dashboard = data;
        if (this.auth.currentUser?.university.is_pathway_provider) {
          dashboard.data.attributes.banner =
            dashboard.data.attributes.IS_carousel;
          dashboard.data.attributes.banner_alt =
            dashboard.data.attributes.IS_carousel_alt;
          dashboard.data.attributes.carousel =
            dashboard.data.attributes.IS_carousel.data.attributes;
          dashboard.data.attributes.carousel_alt =
            dashboard.data.attributes.IS_carousel_alt.data.attributes;
        }

        this.deferUpdate(() => {
          this.parsePageData(dashboard);
          super.setMetas();
        });
      });

    this.auth.getCurrentUser().subscribe({
      next: (theUser: User) => {
        if (theUser) {
          this.deferUpdate(() => {
            this.showWellbeing = !!theUser.accommodation?.wellbeing;
            this.showCYCBanner = !!theUser.accommodation?.covered;
            this.showInternationalStudentCarousel =
              theUser.university.is_pathway_provider;
          });
          this.getInternationalStudentPageData();
        }
      },
    });

    this.getInternationalStudentPageData();
  }

  /**
   * Fetch international student page data for the banner
   * @param void
   * @returns void
   */
  private getInternationalStudentPageData(): void {
    this.strapi.getInternationalStudentPage().subscribe({
      next: (
        internationalStudentData: IStrapiDocument<IInternationalStudentPage>
      ) => {
        this.deferUpdate(() => {
          this.internationalStudentPageData =
            internationalStudentData.data.attributes;
          const usersUniversity = this.auth.currentUser?.university.name ?? '';
          const content =
            this.internationalStudentPageData.dashboard_banner_content;
          this.internationalStudentPageData.dashboard_banner_content =
            content?.replaceAll('<name>', usersUniversity) ?? '';
        });
      },
      error: () => {
        this.deferUpdate(() => {
          this.showInternationalStudentBanner = false;
        });
      },
      complete: () => {
        this.deferUpdate(() => {
          this.showInternationalStudentBanner =
            this.auth.currentUser?.university.is_pathway_provider ?? false;
        });
      },
    });
  }

  /**
   * fetch insurance products for insurance card
   * @param void
   * @returns void
   */
  private getInsuranceProducts(): void {
    this.auth.getUserInsuranceProducts().subscribe({
      next: (insuranceProducts: InsuranceProductsResponse) => {
        this.deferUpdate(() => {
          this.insuranceProducts = insuranceProducts.data;
          this.isLoading = false;
        });
      },
      complete: () => {
        this.deferUpdate(() => {
          this.isLoading = false;
        });
      },
    });
  }

  /**
   * update the users confirm cover if they navigate away from the dashboard
   * @returns void
   */
  private updateConfirmCover(): void {
    if (this.auth.currentUser?.confirmedCover) return;

    const clone = User.fromJson(this.auth.currentUser!);
    clone.confirmedCover = true;

    this.userService.updateUser(clone, false).subscribe({
      next: () => {
        if (this.auth.currentUser) {
          this.auth.currentUser.confirmedCover = true;
          this.auth.afterUserUpdate();
        }
      },
    });
  }

  private handlePageData(user: User): void {
    this.showAccommodationPartnerBanner =
      user.accommodation?.showPartner ?? false;
    this.accommodationPartnerId = user.accommodation?.partner_link_uid;
    this.showUniPartnerBanner = user.university.show_partner ?? false;
    this.uniPartnerId = user.university?.partner_link_uid;
    this.shouldShowOnboarding = !user?.onboarded;
    this.showCYCBanner = !!user.accommodation.covered;

    const allowedSlugs: string[] = [
      AlternateResidence.livingAtHome,
      AlternateResidence.privateRental,
    ];

    if (!user.confirmedCover && user.accommodation.covered) {
      this.shouldShowCYC = true;
      // Update the user to set confirmed cover
      this.updateConfirmCover();
    }

    this.showWellbeing =
      !!user.accommodation.wellbeing ||
      allowedSlugs.includes(user.accommodation.slug);
  }

  public onCoverModalDisplayChange(showModal: boolean): void {
    this.showCYCModal = showModal;
  }

  public onOnboardingModalDisplayChange(showModal: boolean): void {
    this.shouldShowOnboarding = showModal;
  }

  protected override parsePageData(page: IStrapiDocument<IDashboard>): void {
    this.pageData = page.data.attributes;

    super.parsePageData(page.data.attributes);

    this.cycDashboardBanner = page.data.attributes.cyc_banner.data.attributes;

    this.getProtectAndSaveProducts();

    this.getStudentWellbeing();

    this.getStudentLife();

    this.getLatestsOffers();

    if (this.pageData.enable_jobs_and_careers) {
      this.getJobsAndCareers();
    }

    this.showDashboardInsuranceSection = this.showMyInsuranceSection;
  }

  protected getProtectAndSaveProducts(): void {
    this.strapi
      .getProtectAndSaveProducts()
      .subscribe((docs: IStrapiDocuments<ProtectAndSaveProduct>) => {
        const products = docs.data;

        this.protectAndSaveProducts = products
          .map(
            (product) =>
              <DashboardCard>{
                title: product.attributes.name,
                title_alt_img: product.attributes?.dashboard_title_logo,
                description: product.attributes.dashboard_card_excerpt,
                description_alt_img: product.attributes.dashboard_card_logo,
                image: product.attributes.dashboard_card_buddy,
                link: product.attributes.dashboard_card_button_link,
                route: product.attributes.dashboard_card_button_route,
                centre_slide: false,
                show_card: true,
                order_number: product.attributes.order_number ?? 9999,
              }
          )
          .sort(
            (currentItem: DashboardCard, nextItem: DashboardCard) =>
              (currentItem.order_number ?? -1) - (nextItem.order_number ?? -1)
          );
      });
  }

  protected getLatestsOffers(): void {
    if (!this.pageData?.enable_offers_section) return;

    this.strapi.getOffers().subscribe((data: IStrapiDocument<IOffers>) => {
      const offers = data.data.attributes;
      if (offers?.cards) {
        this.latestOffersCards = offers?.cards
          .filter((offer: IOfferCard) => offer.show_card)
          .map((offer: IOfferCard) => {
            const tempCard: DashboardCard = {
              title: offer.card_title,
              title_alt_img: null,
              description: offer.card_excerpt,
              description_alt_img: null,
              image: offer.icon,
              link: offer.external_link,
              route: offer.web_route,
              show_helpline: false,
              centre_slide: offer.centre_slide,
              show_card: offer.show_card,
              button_text: offer.card_button_text,
              order_number: -1,
            };
            return tempCard;
          });
      }
    });
  }

  protected getStudentLife(): void {
    this.strapi
      .getDocumentsByType('student-life-articles', {
        populate: '*',
      })
      .subscribe((data: IStrapiDocuments<StudentLifeArticleCard>) => {
        this.studentLifeCards = data.data
          .filter((article) => {
            const cardData = article.attributes;
            return cardData.show_article;
          })
          .map((article) => {
            const studentLifeArticle = article.attributes;
            studentLifeArticle.uid = article.attributes.uid ?? '';
            return studentLifeArticle;
          })
          .sort(
            (currentItem, nextItem) =>
              new Date(nextItem.date_published).getTime() -
              new Date(currentItem.date_published).getTime()
          );
      });
  }

  protected getStudentWellbeing(): void {
    this.strapi
      .getDocumentsByType('student-wellbeings', { populate: '*' })
      .subscribe((data: IStrapiDocuments<StudentWellbeing>) => {
        this.studentWellbeingCards = data.data;

        this.studentWellbeingCards = this.studentWellbeingCards
          .filter(
            (card: IDocument<StudentWellbeing>) => card.attributes.show_card
          )
          .map((card: IDocument<StudentWellbeing>) => {
            const tempCard: DashboardCard = {
              title: card.attributes.title,
              title_alt_img: null,
              description: card.attributes.description,
              description_alt_img: null,
              image: card.attributes.image,
              link: null,
              route: card.attributes.route,
              show_helpline: card.attributes.show_helpline,
              centre_slide: false,
              show_card: card.attributes.show_card,
              button_text: 'Find out more',
              order_number: card.attributes.order_number,
            };
            return tempCard;
          });

        const showHelpline = this.studentWellbeingCards.some(
          (card: DashboardCard) => card.show_helpline === true
        );

        this.studentWellbeingCards.sort(
          (currentItem: DashboardCard, nextItem: DashboardCard) =>
            (currentItem.order_number ?? -1) - (nextItem.order_number ?? -1)
        );

        if (showHelpline) {
          this.strapi
            .getDocumentByUID('helplines', 'helplines')
            .subscribe((d: IStrapiDocuments<HelplineData>) => {
              this.helplineData = d.data[0].attributes;
            });
        }
      });
  }

  /**
   * Get the jobs and careers dashboard cards
   */
  protected getJobsAndCareers(): void {
    this.strapi
      .getJobsAndCareersCarousel()
      .subscribe((data: IStrapiDocument<IJobsAndCareers>) => {
        this.jobsAndCareersCards = data.data.attributes.dashboard_cards;
      });
  }

  public onNavigate(route: string): void {
    this.router.navigateByUrl(route).finally(() => false);
  }

  /**
   * decide if we should show the my insurance section
   */
  public get showMyInsuranceSection(): boolean {
    if (
      this.auth.currentUser?.university?.is_unihome_product &&
      !this.auth.hasStudentGuardInsurance &&
      !this.auth.currentUser?.insuranceProducts?.length
    ) {
      return false;
    }

    if (this.pageData.enable_my_insurance_section) {
      return true;
    }

    return false;
  }

  /**
   * decide if we should show the protect and save section
   */
  public get protectAndSaveSection(): boolean {
    return (
      this.pageData?.enable_protect_and_save_section &&
      !this.auth.currentUser?.university?.is_unihome_product
    );
  }

  get showStudentGuardBanner() {
    return (
      this.pageData.student_guard_banner?.data?.attributes &&
      this.auth.currentUser?.university?.is_unihome_product &&
      this.pageData?.enable_student_guard
    );
  }
}
